import type { AssetLibraryDisplayItem } from './utils';

export type AssetLibraryMediaKind = 'media-image' | 'media-video';

export type AssetLibraryMediaSelection = {
  kind: AssetLibraryMediaKind;
  mediaID: string;
  assetID: string;
  label: string;
  thumbnailURL: string | null;
};

export function toAssetLibraryMediaSelection(
  asset: AssetLibraryDisplayItem,
): AssetLibraryMediaSelection | null {
  if (asset.assetType === 'MEDIA_IMAGE') {
    return {
      kind: 'media-image',
      mediaID: asset.assetID,
      assetID: asset.assetID,
      label: asset.assetName,
      thumbnailURL: asset.thumbnailURL,
    };
  }

  if (asset.assetType === 'MEDIA_VIDEO') {
    return {
      kind: 'media-video',
      mediaID: asset.assetID,
      assetID: asset.assetID,
      label: asset.assetName,
      thumbnailURL: asset.thumbnailURL,
    };
  }

  return null;
}

/** `video-and-image` pickers stage videos and stills in one selection. */
export type MediaPickerKind = 'image' | 'video' | 'video-and-image';

/** Comic stills are jpg/png only; gif/webp stay out of mixed pickers. */
export const JPG_PNG_STILL_EXTENSIONS = ['.jpg', '.jpeg', '.png'] as const;

function getNameOrUrlExtension(value: string): string {
  const match = value.toLowerCase().match(/(\.[a-z0-9]+)(?:[?#]|$)/);
  return match ? match[1] : '';
}

/**
 * Videos are always allowed. Images must look like jpg/jpeg/png so gif/webp
 * library assets cannot be staged in mixed comic pickers.
 */
export function isVideoOrJpgPngStillMedia(asset: {
  assetType?: string;
  kind?: AssetLibraryMediaKind;
  assetName?: string;
  label?: string;
  thumbnailURL?: string | null;
}): boolean {
  if (asset.kind === 'media-video' || asset.assetType === 'MEDIA_VIDEO') {
    return true;
  }

  if (asset.kind !== 'media-image' && asset.assetType !== 'MEDIA_IMAGE') {
    return false;
  }

  const extension =
    getNameOrUrlExtension(asset.assetName ?? '') ||
    getNameOrUrlExtension(asset.label ?? '') ||
    getNameOrUrlExtension(asset.thumbnailURL ?? '');

  return (JPG_PNG_STILL_EXTENSIONS as readonly string[]).includes(extension);
}

export function getMediaPickerTitleSuffix(mediaKind: MediaPickerKind): string {
  if (mediaKind === 'video') return ' (videos)';
  if (mediaKind === 'image') return ' (images)';
  return ' (videos & images)';
}

export function getMediaPickerConfirmLabel(input: {
  mediaKind: MediaPickerKind;
  count: number;
}): string {
  const noun =
    input.mediaKind === 'video'
      ? 'video'
      : input.mediaKind === 'image'
        ? 'image'
        : 'file';
  const plural = input.count === 1 ? noun : `${noun}s`;
  return `Add ${plural} (${input.count})`;
}
